import React, { useState } from "react";
import { Button, Card, Col, message, Modal, Popconfirm, Progress, Row, Select, Statistic, Table } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { hvoFirestore, vceFirestore } from "configs/FirebaseConfig";
import {
  FIRESTORE_ANALYSIS_RFM_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_USERS_TABLE,
} from "constants/FirestoreConstant";
import { DeleteOutlined, EyeOutlined, PlayCircleOutlined } from "@ant-design/icons";
import urls from "urls";
import Loading from "components/shared-components/Loading";

const { Option } = Select;

const RFMDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState("");
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("6months");
  const [selectedShopSource, setSelectedShopSource] = useState("VCE");
  const [deletingAnalysis, setDeletingAnalysis] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [analysesLoading, setAnalysesLoading] = useState(true);
  const [shopSourceFilter, setShopSourceFilter] = useState("all");
  const { t } = useTranslation();
  const navigate = useNavigate();

  const BATCH_SIZE = 500;

  const periodOptions = [
    { value: "6months", label: t("6_months"), months: 6 },
    { value: "1year", label: t("1_year"), months: 12 },
    { value: "1.5years", label: t("1_5_years"), months: 18 },
    { value: "2years", label: t("2_years"), months: 24 },
  ];

  const getPeriodLabel = (period) => {
    const option = periodOptions.find((opt) => opt.value === period);
    return option ? option.label : period;
  };

  const getPeriodMonths = (period) => {
    const option = periodOptions.find((opt) => opt.value === period);
    return option ? option.months : null;
  };

  React.useEffect(() => {
    fetchAnalyses();
  }, [shopSourceFilter]);

  const fetchAnalyses = async () => {
    setAnalysesLoading(true);
    try {
      const allAnalyses = [];

      if (shopSourceFilter === "all" || shopSourceFilter === "VCE") {
        const vceSnap = await vceFirestore.collection(FIRESTORE_ANALYSIS_RFM_TABLE).orderBy("timestamp", "desc").get();
        vceSnap.docs.forEach((doc) => {
          allAnalyses.push({ id: doc.id, ...doc.data(), shopSource: "VCE" });
        });
      }

      if (shopSourceFilter === "all" || shopSourceFilter === "HVO") {
        const hvoSnap = await hvoFirestore.collection(FIRESTORE_ANALYSIS_RFM_TABLE).orderBy("timestamp", "desc").get();
        hvoSnap.docs.forEach((doc) => {
          allAnalyses.push({ id: doc.id, ...doc.data(), shopSource: "HVO" });
        });
      }

      allAnalyses.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setAnalyses(allAnalyses);
    } catch (error) {
      console.error("Error fetching analyses:", error);
      message.error(t("error_loading_analyses"));
    } finally {
      setAnalysesLoading(false);
    }
  };

  const fetchOrdersData = async (period, shopSource) => {
    setLoadingStatus(t("fetching_orders_data"));
    setLoadingProgress(10);

    try {
      const firestore = shopSource === "VCE" ? vceFirestore : hvoFirestore;
      let ordersQuery = firestore.collection(FIRESTORE_ORDERS_TABLE);

      if (period !== "alltime") {
        const months = getPeriodMonths(period);
        const periodAgo = dayjs().subtract(months, "months").unix();
        ordersQuery = ordersQuery.where("createdAt", ">=", periodAgo);
      }

      const ordersSnapshot = await ordersQuery.get();

      setLoadingProgress(50);
      setLoadingStatus(t("processing_orders"));

      const allOrders = [];
      ordersSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.orderStatus !== "CREATED") {
          allOrders.push({ id: doc.id, ...data });
        }
      });

      console.log(`Fetched ${allOrders.length} orders for period: ${period}, shop: ${shopSource}`);
      return allOrders;
    } catch (error) {
      console.error("Error fetching orders:", error);
      throw error;
    }
  };

  const fetchUsersData = async (shopSource) => {
    setLoadingStatus(t("fetching_users_data"));
    setLoadingProgress(60);

    try {
      const firestore = shopSource === "VCE" ? vceFirestore : hvoFirestore;
      const usersQuery = await firestore.collection(FIRESTORE_USERS_TABLE).where("role", "==", "webshopUser").get();

      setLoadingProgress(70);
      setLoadingStatus(t("processing_users_data"));

      const users = [];
      usersQuery.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() });
      });

      console.log(`Fetched ${users.length} webshop users for shop: ${shopSource}`);
      return users;
    } catch (error) {
      console.error("Error fetching users:", error);
      throw error;
    }
  };

  const runNewAnalysis = async () => {
    console.log(`Starting new RFM analysis for period: ${selectedPeriod}, shop: ${selectedShopSource}...`);
    setLoading(true);
    setLoadingProgress(0);
    setShowPeriodModal(false);

    try {
      const orders = await fetchOrdersData(selectedPeriod, selectedShopSource);
      const users = await fetchUsersData(selectedShopSource);

      setLoadingStatus(t("calculating_rfm_metrics"));
      setLoadingProgress(80);

      const processInBatches = async (orders, users) => {
        const customerOrderMap = {};

        for (let i = 0; i < orders.length; i += BATCH_SIZE) {
          const batch = orders.slice(i, i + BATCH_SIZE);

          batch.forEach((order) => {
            const email = order.customer?.email;
            if (email && order.customer?.uid !== "0") {
              if (!customerOrderMap[email]) {
                customerOrderMap[email] = [];
              }
              customerOrderMap[email].push(order);
            }
          });

          if (i % (BATCH_SIZE * 2) === 0) {
            await new Promise((resolve) => setTimeout(resolve, 10));
          }
        }

        return customerOrderMap;
      };

      const customerOrderMap = await processInBatches(orders, users);
      const now = dayjs().unix();

      setLoadingStatus(t("finalizing_calculations"));
      setLoadingProgress(90);

      const rfmResults = Object.entries(customerOrderMap).map(([email, customerOrders]) => {
        const userData = users.find((user) => user.email === email);

        let lastOrderTime;
        if (userData && userData.lastOrderTimestamp) {
          lastOrderTime = userData.lastOrderTimestamp;
        } else {
          lastOrderTime = Math.max(...customerOrders.map((o) => o.createdAt));
        }
        const recency = Math.floor((now - lastOrderTime) / (24 * 60 * 60));

        const periodMonths =
          getPeriodMonths(selectedPeriod) ||
          (selectedPeriod === "alltime"
            ? Math.max(1, Math.ceil((now - Math.min(...customerOrders.map((o) => o.createdAt))) / (30 * 24 * 60 * 60)))
            : 6);
        const frequency = (customerOrders.length / periodMonths).toFixed(2);

        const totalSpent = customerOrders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);
        const monetary = (totalSpent / customerOrders.length).toFixed(2);

        return {
          email,
          recency,
          frequency: parseFloat(frequency),
          monetary: parseFloat(monetary),
          totalOrders: customerOrders.length,
          totalSpent,
        };
      });

      // Percentile-based histogram voor recency en monetary.
      // Gebruikt p5–p95 als bucketrange zodat outliers de schaal niet oprekken.
      // De eerste bucket vangt alles onder p5, de laatste alles boven p95.
      const createDistribution = (data, key, buckets = 20) => {
        const values = data.map((item) => item[key]).sort((a, b) => a - b);

        const p5Index = Math.floor(values.length * 0.05);
        const p95Index = Math.floor(values.length * 0.95);
        const rangeMin = values[p5Index];
        const rangeMax = values[p95Index];
        const bucketSize = (rangeMax - rangeMin) / (buckets - 1);

        return Array.from({ length: buckets }, (_, i) => {
          const bucketMin = rangeMin + i * bucketSize;
          const bucketMax = rangeMin + (i + 1) * bucketSize;
          const isFirst = i === 0;
          const isLast = i === buckets - 1;

          const count = values.filter((v) => {
            if (isFirst) return v < bucketMax;
            if (isLast) return v >= bucketMin;
            return v >= bucketMin && v < bucketMax;
          }).length;

          let range;
          if (key === "recency") {
            range = isLast
              ? `${Math.floor(bucketMin)}+ ${t("days")}`
              : `${Math.floor(bucketMin)}-${Math.floor(bucketMax)} ${t("days")}`;
          } else {
            // monetary
            range = isLast ? `€${bucketMin.toFixed(0)}+` : `€${bucketMin.toFixed(0)}-€${bucketMax.toFixed(0)}`;
          }

          return { range, count, bucketMin };
        });
      };

      // Frequency: groepeer op aantal orders in plaats van een rate-histogram.
      // Orders/maand geeft slechts een handvol discrete waarden (1 order = 0.08,
      // 2 orders = 0.17, ...) waardoor de meeste buckets leeg blijven.
      // Een staafdiagram per orderaantal is veel informatiever.
      const createFrequencyDistribution = (data) => {
        const MAX_INDIVIDUAL = 9; // 1–9 apart weergeven, daarna "10+"
        const orderCounts = {};

        data.forEach((item) => {
          const bucket = item.totalOrders >= MAX_INDIVIDUAL + 1 ? `${MAX_INDIVIDUAL + 1}+` : String(item.totalOrders);
          orderCounts[bucket] = (orderCounts[bucket] || 0) + 1;
        });

        const bucketOrder = [
          ...Array.from({ length: MAX_INDIVIDUAL }, (_, i) => String(i + 1)),
          `${MAX_INDIVIDUAL + 1}+`,
        ];

        return bucketOrder.map((bucket) => ({
          range: `${bucket} ${t("orders")}`,
          count: orderCounts[bucket] || 0,
          bucketMin: parseInt(bucket) || MAX_INDIVIDUAL + 1,
        }));
      };

      const distributions = {
        recency: createDistribution(rfmResults, "recency"),
        frequency: createFrequencyDistribution(rfmResults),
        monetary: createDistribution(rfmResults, "monetary"),
      };

      const calculatePercentiles = (data, key) => {
        const sorted = data.map((item) => item[key]).sort((a, b) => a - b);
        return {
          p20: sorted[Math.floor(sorted.length * 0.2)],
          p40: sorted[Math.floor(sorted.length * 0.4)],
          p60: sorted[Math.floor(sorted.length * 0.6)],
          p80: sorted[Math.floor(sorted.length * 0.8)],
          min: sorted[0],
          max: sorted[sorted.length - 1],
        };
      };

      const percentiles = {
        recency: calculatePercentiles(rfmResults, "recency"),
        frequency: calculatePercentiles(rfmResults, "totalOrders"), // ← totalOrders ipv frequency
        monetary: calculatePercentiles(rfmResults, "monetary"),
      };

      const customerOrders = orders.filter((order) => order.customer?.uid !== "0");
      const guestOrders = orders.filter((order) => order.customer?.uid === "0");

      const customerPercentage = orders.length > 0 ? ((customerOrders.length / orders.length) * 100).toFixed(1) : "0";
      const guestPercentage = orders.length > 0 ? ((guestOrders.length / orders.length) * 100).toFixed(1) : "0";

      const orderStats = {
        customerOrders: customerOrders.length,
        guestOrders: guestOrders.length,
        customerPercentage,
        guestPercentage,
      };

      const analysisData = {
        timestamp: dayjs().unix(),
        name: dayjs().format("MMMM D, YYYY HH:mm"),
        period: selectedPeriod,
        periodLabel: getPeriodLabel(selectedPeriod),
        totalCustomers: rfmResults.length,
        totalActiveCustomers: users.length,
        totalOrdersAnalyzed: orders.length,
        activationRate: ((rfmResults.length / users.length) * 100).toFixed(1),
        orderStats,
        percentiles,
        distributions,
      };

      setLoadingStatus(t("saving_analysis"));
      setLoadingProgress(95);

      const firestore = selectedShopSource === "VCE" ? vceFirestore : hvoFirestore;
      const docRef = await firestore.collection(FIRESTORE_ANALYSIS_RFM_TABLE).add(analysisData);

      setLoadingProgress(100);
      setLoadingStatus(t("analysis_complete"));

      message.success(t("analysis_saved_successfully"));

      setTimeout(() => {
        navigate(`${urls.rfmAnalysis}/${docRef.id}?shopSource=${selectedShopSource}`);
      }, 1000);
    } catch (error) {
      console.error("Error running analysis:", error);
      setLoadingStatus(`Error: ${error.message}`);
      message.error(t("analysis_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleStartAnalysis = () => {
    setShowPeriodModal(true);
  };

  const handlePeriodConfirm = () => {
    runNewAnalysis();
  };

  const handleDeleteAnalysis = async (analysis) => {
    try {
      setDeletingAnalysis(analysis.id);
      const firestore = analysis.shopSource === "VCE" ? vceFirestore : hvoFirestore;
      await firestore.collection(FIRESTORE_ANALYSIS_RFM_TABLE).doc(analysis.id).delete();
      message.success(t("analysis_deleted_successfully"));
      fetchAnalyses();
    } catch (error) {
      console.error("Error deleting analysis:", error);
      message.error(t("error_deleting_analysis"));
    } finally {
      setDeletingAnalysis(null);
    }
  };

  const columns = [
    {
      title: t("shop"),
      dataIndex: "shopSource",
      key: "shopSource",
      render: (shopSource) => (
        <span style={{ fontWeight: 500, color: shopSource === "VCE" ? "#1890ff" : "#eb2f96" }}>{shopSource}</span>
      ),
      width: 80,
    },
    {
      title: t("analysis_name"),
      dataIndex: "name",
      render: (name, record) => (
        <div>
          <div>RFM Analysis - {name}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {t("period")}: {record.periodLabel || t("6_months")}
          </div>
        </div>
      ),
      width: 300,
    },
    {
      title: t("date"),
      dataIndex: "timestamp",
      render: (timestamp) => dayjs.unix(timestamp).format("MMMM D, YYYY HH:mm"),
      width: 200,
    },
    {
      title: t("period"),
      dataIndex: "periodLabel",
      render: (periodLabel) => periodLabel || t("6_months"),
      width: 120,
    },
    {
      title: t("customers_analyzed"),
      dataIndex: "totalCustomers",
      width: 150,
    },
    {
      title: t("activation_rate"),
      dataIndex: "activationRate",
      render: (rate) => `${rate}%`,
      width: 150,
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => navigate(`${urls.rfmAnalysis}/${record.id}?shopSource=${record.shopSource}`)}
          >
            {t("view")}
          </Button>
          <Popconfirm
            title={t("delete_analysis")}
            description={t("are_you_sure_delete_analysis")}
            onConfirm={() => handleDeleteAnalysis(record)}
            okText={t("yes")}
            cancelText={t("no")}
            okButtonProps={{ danger: true }}
          >
            <Button
              icon={<DeleteOutlined />}
              loading={deletingAnalysis === record.id}
              disabled={deletingAnalysis === record.id}
              danger
            />
          </Popconfirm>
        </div>
      ),
      width: 200,
    },
  ];

  if (analysesLoading) {
    return <Loading cover="content" />;
  }

  return (
    <div style={{ padding: "24px" }}>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card style={{ minWidth: 400 }}>
            <div style={{ textAlign: "center" }}>
              <Progress percent={loadingProgress} />
              <p style={{ marginTop: "10px" }}>{loadingStatus}</p>
              <p className="text-muted">{t("processing_batches", { size: BATCH_SIZE })}</p>
            </div>
          </Card>
        </div>
      )}

      <Modal
        title={t("select_analysis_period")}
        open={showPeriodModal}
        onOk={handlePeriodConfirm}
        onCancel={() => setShowPeriodModal(false)}
        okText={t("start_analysis")}
        cancelText={t("cancel")}
      >
        <div style={{ marginBottom: "16px" }}>
          <p>{t("select_period_description")}</p>
        </div>
        <Select style={{ width: "100%", marginBottom: 16 }} value={selectedShopSource} onChange={setSelectedShopSource}>
          <Option value="VCE">VCE - Vleescentrum</Option>
          <Option value="HVO">HVO - Halalvlees Online</Option>
        </Select>
        <Select style={{ width: "100%" }} value={selectedPeriod} onChange={setSelectedPeriod} options={periodOptions} />
      </Modal>

      <Row gutter={16} className="mb-4">
        <Col span={12}>
          <h2>{t("rfm_analysis_dashboard")}</h2>
          <p>{t("manage_rfm_analysis")}</p>
        </Col>
        <Col span={6}>
          <Select
            placeholder={t("shop_source")}
            value={shopSourceFilter}
            onChange={setShopSourceFilter}
            style={{ width: "100%" }}
          >
            <Option value="all">{t("all")}</Option>
            <Option value="VCE">VCE</Option>
            <Option value="HVO">HVO</Option>
          </Select>
        </Col>
        <Col span={6} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            size="large"
            icon={<PlayCircleOutlined />}
            onClick={handleStartAnalysis}
            loading={loading}
            disabled={loading}
          >
            {loading ? t("analyzing") : t("run_new_analysis")}
          </Button>
        </Col>
      </Row>

      {analyses.length > 0 && (
        <Row gutter={16} className="mb-4">
          <Col span={6}>
            <Card>
              <Statistic title={t("total_analyses")} value={analyses.length} />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title={t("latest_analysis")} value={dayjs.unix(analyses[0]?.timestamp).format("MMM D")} />
            </Card>
          </Col>
        </Row>
      )}

      <Card title={t("analysis_history")}>
        {analyses.length === 0 ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <p>{t("no_analyses_yet")}</p>
            <p className="text-muted">{t("run_first_analysis_to_get_started")}</p>
          </div>
        ) : (
          <Table
            showSorterTooltip={false}
            columns={columns}
            dataSource={analyses}
            rowKey={(record) => `${record.shopSource}-${record.id}`}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 800 }}
          />
        )}
      </Card>
    </div>
  );
};

export default RFMDashboard;
